import { FromToLabel, SearchParam, SearchParamType } from '@se/platform';
import moment from 'moment/moment';
import { IAppliedFilterChip } from '../ap-search-filter/types';

export const getFromToValue = (ftValue: FromToLabel): string | null => {
    if (ftValue.from && ftValue.to) {
        const from = moment.isMoment(ftValue.from)
            ? ftValue.from.format('DD/MM/YYYY')
            : moment(ftValue.from).format('DD/MM/YYYY');
        const to = moment.isMoment(ftValue.to)
            ? ftValue.to.format('DD/MM/YYYY')
            : moment(ftValue.to).format('DD/MM/YYYY');
        return `${from} - ${to}`;
    } else if (ftValue.from) {
        const from = moment.isMoment(ftValue.from)
            ? ftValue.from.format('DD/MM/YYYY')
            : moment(ftValue.from).format('DD/MM/YYYY');
        return `${from} - ___`;
    } else if (ftValue.to) {
        const to = moment.isMoment(ftValue.to)
            ? ftValue.to.format('DD/MM/YYYY')
            : moment(ftValue.to).format('DD/MM/YYYY');
        return `___ - ${to}`;
    } else {
        return null;
    }
};
export const searchParamFilterToAppliedChip = (searchParam: SearchParam): IAppliedFilterChip => {
    let value = searchParam.property.value;
    const data = searchParam.property.itemsSourceInfo?.data || [];
    const displayProperty = searchParam.property.itemsSourceInfo?.displayProperty || '';
    const ftValue = value as FromToLabel;
    switch (searchParam.property.type) {
        case SearchParamType.fromTo:
            value = getFromToValue(ftValue);
            break;
        case SearchParamType.date:
            value = moment.isMoment(value) ? value.format('dd/MM/yyyy') : value;
            break;
        case SearchParamType.time:
            value = moment.isMoment(value) ? value.format('hh:mm') : value;
            break;
        case SearchParamType.select:
        case SearchParamType.filterSelect: {
            if (data.length > 0 && displayProperty) {
                const item = data.find((itm) => itm.guid === searchParam.property.value);
                value = item?.[displayProperty];
            }
            break;
        }
        case SearchParamType.multiSelect:
        case SearchParamType.filterMultiSelect: {
            if (data.length > 0 && displayProperty) {
                value = searchParam.property.value
                    .map((id: string) => data.find((item) => item.guid === id))
                    .filter(Boolean)
                    .map((item: { [x: string]: any }) => item[displayProperty]);
            }
            break;
        }
    }
    return {
        labelTranslationKey: labelTranslationKeyToString(searchParam.labelTranslationKey),
        filterName: searchParam.property.name,
        value,
        primary: searchParam.layout?.row === 0,
        row: searchParam.layout?.row ?? 0,
        column: searchParam.layout?.column ?? 0,
    };
};
export const searchParamIsSet = (searchParam: SearchParam): boolean => {
    const value = searchParam.property.value;
    if (moment.isMoment(value)) return true;
    const isArray = Array.isArray(value);
    if (isArray) return value.length > 0;
    if (typeof value === 'object' && value) {
        return value.from || value.to;
    }
    return value === 0 || Boolean(value);
};
export const getPrimarySearchParamGroupedByRow = (searchParams: SearchParam[][]) =>
    searchParams.flat().filter((param) => param.layout?.row === 0);
export const getSecondarySearchParamGroupedByRow = (searchParams: SearchParam[][]) =>
    searchParams.map((params) => params.filter((param) => param.layout?.row !== 0));
export const filterListToChip = (filterList: SearchParam[]) =>
    filterList
        .filter(searchParamIsSet)
        .map(searchParamFilterToAppliedChip)
        .filter((item) => item.value !== null);

export const labelTranslationKeyToString = (labelTranslationKey: FromToLabel | string | undefined): string => {
    if (!labelTranslationKey) return '';
    if (typeof labelTranslationKey === 'string') return labelTranslationKey;
    if (labelTranslationKey.label) return labelTranslationKey.label;
    return `${labelTranslationKey.from} - ${labelTranslationKey.to}`;
};

export const labelTranslationKeyToFromToLabel = (
    labelTranslationKey: FromToLabel | string | undefined,
): FromToLabel => {
    if (labelTranslationKey === undefined || typeof labelTranslationKey === 'string')
        return {
            label: 'from to',
            from: 'from',
            to: 'to',
        };
    return labelTranslationKey;
};

export const adjustFilters = (filters: SearchParam[]): SearchParam[] => {
    const _primary = filters.filter((item) => item.layout?.row === 0);
    _primary.sort((a, b) => (a.layout?.column ?? 0) - (b.layout?.column ?? 0));
    const primary = _primary.map((item, index) => ({
        ...item,
        layout: {
            ...item.layout,
            row: 0,
            column: index,
        },
    }));
    const _secondary = filters.filter((item) => item.layout && item.layout.row > 0);
    _secondary.sort(
        (a, b) => (a.layout?.row ?? 0) - (b.layout?.row ?? 0) || (a.layout?.column ?? 0) - (b.layout?.column ?? 0),
    );
    const secondary = _secondary.map((item, index) => ({
        ...item,
        layout: {
            ...item.layout,
            row: 1,
            column: index,
        },
    }));
    return [...primary, ...secondary];
};

export const removeData = (filter: SearchParam): SearchParam => {
    return {
        ...filter,
        property: {
            ...filter.property,
            itemsSourceInfo: filter.property.itemsSourceInfo
                ? {
                      ...filter.property.itemsSourceInfo,
                      dataLoading: undefined,
                      data: undefined,
                      originalData: undefined,
                  }
                : undefined,
        },
    };
};

export const userSearchParam = (filter: SearchParam): SearchParam => {
    return {
        property: {
            name: filter.property.name, // ID
            value: filter.property?.value,
            defaultValue: filter.property?.defaultValue,
        },
        layout: filter.layout,
    };
};

export const siteSearchParam = (filter: SearchParam): SearchParam => {
    return {
        active: filter.active,
        property: {
            name: filter.property.name, // ID
            type: filter.property.type,
            itemsSourceInfo: filter.property?.itemsSourceInfo,
        },
        labelTranslationKey: filter.labelTranslationKey,
    };
};

/*
export interface UserSearchParam {
    layout: ISearchParamLayout;
    property: ISearchParamProperty;
}

export interface SiteSearchParam {
    active?: boolean; // Filtro usato o no (compare scompare dall'interfaccia)
    labelTranslationKey: FromToLabel | string;
    property: ISearchParamProperty;
}
export interface SearchParam  {
    active ?: boolean; // Filtro usato o no (compare scompare dall'interfaccia)
    labelTranslationKey: FromToLabel | string;
    layout: ISearchParamLayout;
    property: ISearchParamProperty;
}

export interface ISearchParamProperty {
    name: string; // ID
    value: any;
    defaultValue?: any;
    type: SearchParamType;
    itemsSourceInfo?: ISearchParamItemsSource;
}
*/
